import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAnimation } from '../contexts/AnimationContext';
import { getDateLocale } from '../i18n';
import { getCurrentUserProfile, getTenantSubdomain } from '../services/api';
import {
  getImputationEtatOptions,
  getDefaultEtatForNewImputation,
  getEtatBadgeClasses,
  getEtatLabel,
} from '../utils/imputationEtats';
import './Login.css';

const MyImputations = () => {
  const { t, i18n } = useTranslation('employee');
  const { t: tCommon } = useTranslation('common');
  const { useTransformersAnimation } = useAnimation();

  // Current user (for role check when reference is disabled)
  const [currentUser, setCurrentUser] = useState(null);

  // State for filters
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    gcr: '',
    operation: '',
    production_line_lp: '',
  });

  // State for modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showOperationDetailsModal, setShowOperationDetailsModal] = useState(false);
  const [showChartContainer, setShowChartContainer] = useState(false);

  // State for selected operation details
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [selectedImputation, setSelectedImputation] = useState(null);

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Mock data - replace with API calls
  const [imputations, setImputations] = useState([]);
  const [operations, setOperations] = useState([]);
  const [totalPages, setTotalPages] = useState(1);

  // Operation search state - MOVED BEFORE useEffect to fix initialization order
  const [operationSearch, setOperationSearch] = useState('');
  const [showOperationDropdown, setShowOperationDropdown] = useState(false);
  const [filterOperationSearch, setFilterOperationSearch] = useState('');
  const [showFilterOperationDropdown, setShowFilterOperationDropdown] = useState(false);

  // Mock operations data
  useEffect(() => {
    // Replace with actual API call
    setOperations([
      { id: 1, code: 'OP001', name: 'Operation 1', production_line_lp: 'PL-1', temps_standard: 2.5, description: 'Description 1', is_active: true },
      { id: 2, code: 'OP002', name: 'Operation 2', production_line_lp: 'PL-2', temps_standard: 3.0, description: 'Description 2', is_active: true },
      { id: 3, code: 'OP003', name: 'Operation 3', production_line_lp: 'PL-1', temps_standard: 1.5, description: 'Description 3', is_active: true },
    ]);

    // Seed imputations data
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const twoDaysAgo = new Date(now);
    twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
    const threeDaysAgo = new Date(now);
    threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
    const weekAgo = new Date(now);
    weekAgo.setDate(weekAgo.getDate() - 7);

    setImputations([
      {
        id: 1,
        date_operation: now.toISOString().split('T')[0],
        gcr: 'GCR-12345',
        operation: { id: 1, code: 'OP001', name: 'Operation 1', production_line_lp: 'PL-1' },
        etat_final: 'TERMINE',
        temps_standard: 2.5,
        date_saisie: now.toISOString(),
        can_be_modified: true,
        commentaire: 'Imputation en cours de traitement'
      },
      {
        id: 2,
        date_operation: yesterday.toISOString().split('T')[0],
        gcr: 'GCR-12346',
        operation: { id: 2, code: 'OP002', name: 'Operation 2', production_line_lp: 'PL-2' },
        etat_final: 'TERMINE',
        temps_standard: 3.0,
        date_saisie: yesterday.toISOString(),
        can_be_modified: true,
        commentaire: 'Opération terminée avec succès'
      },
      {
        id: 3,
        date_operation: twoDaysAgo.toISOString().split('T')[0],
        gcr: 'GIR-78901',
        operation: { id: 3, code: 'OP003', name: 'Operation 3', production_line_lp: 'PL-1' },
        etat_final: 'TERMINE',
        temps_standard: 1.5,
        date_saisie: twoDaysAgo.toISOString(),
        can_be_modified: false,
        commentaire: ''
      },
      {
        id: 4,
        date_operation: threeDaysAgo.toISOString().split('T')[0],
        gcr: 'GCR-12347',
        operation: { id: 1, code: 'OP001', name: 'Operation 1', production_line_lp: 'PL-1' },
        etat_final: 'ECHEC',
        temps_standard: 1.25,
        date_saisie: threeDaysAgo.toISOString(),
        can_be_modified: false,
        commentaire: 'Échec de l\'opération, temps réduit à 50%'
      },
      {
        id: 5,
        date_operation: weekAgo.toISOString().split('T')[0],
        gcr: 'GCR-12348',
        operation: { id: 2, code: 'OP002', name: 'Operation 2', production_line_lp: 'PL-2' },
        etat_final: 'TERMINE',
        temps_standard: 3.0,
        date_saisie: weekAgo.toISOString(),
        can_be_modified: false,
        commentaire: 'Opération complétée la semaine dernière'
      },
      {
        id: 6,
        date_operation: now.toISOString().split('T')[0],
        gcr: 'GIR-78902',
        operation: { id: 3, code: 'OP003', name: 'Operation 3', production_line_lp: 'PL-1' },
        etat_final: 'EN_ATTENTE',
        temps_standard: 1.5,
        date_saisie: now.toISOString(),
        can_be_modified: true,
        commentaire: 'En attente de validation'
      },
      {
        id: 7,
        date_operation: yesterday.toISOString().split('T')[0],
        gcr: 'GCR-12349',
        operation: { id: 1, code: 'OP001', name: 'Operation 1' },
        etat_final: 'ANNULE',
        temps_standard: 0,
        date_saisie: yesterday.toISOString(),
        can_be_modified: true,
        commentaire: 'Opération annulée par l\'utilisateur'
      },
      {
        id: 8,
        date_operation: twoDaysAgo.toISOString().split('T')[0],
        gcr: 'GCR-12350',
        operation: { id: 2, code: 'OP002', name: 'Operation 2' },
        etat_final: 'TERMINE',
        temps_standard: 3.0,
        date_saisie: twoDaysAgo.toISOString(),
        can_be_modified: false,
        commentaire: 'Opération standard terminée'
      }
    ]);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    // #region agent log
    // fetch('http://127.0.0.1:7246/ingest/ef873a82-9c52-4e5a-9883-1a70679064d1',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MyImputations.js:useEffect-clickOutside',message:'useEffect for clickOutside initialized',data:{showOperationDropdown:typeof showOperationDropdown,showFilterOperationDropdown:typeof showFilterOperationDropdown},timestamp:Date.now(),sessionId:'debug-session',runId:'initial',hypothesisId:'A'})}).catch(()=>{});
    // #endregion agent log
    const handleClickOutside = (event) => {
      // #region agent log
      // fetch('http://127.0.0.1:7246/ingest/ef873a82-9c52-4e5a-9883-1a70679064d1',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'MyImputations.js:handleClickOutside',message:'handleClickOutside called',data:{showOperationDropdown:showOperationDropdown,showFilterOperationDropdown:showFilterOperationDropdown},timestamp:Date.now(),sessionId:'debug-session',runId:'initial',hypothesisId:'A'})}).catch(()=>{});
      // #endregion agent log
      if (showOperationDropdown && !event.target.closest('#modal_operation_search') && !event.target.closest('.absolute.z-50')) {
        setShowOperationDropdown(false);
      }
      if (showFilterOperationDropdown && !event.target.closest('#filter_operation_search') && !event.target.closest('.absolute.z-50')) {
        setShowFilterOperationDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOperationDropdown, showFilterOperationDropdown]);

  const etatChoices = React.useMemo(() => getImputationEtatOptions(getTenantSubdomain()), []);

  // Form state for create modal
  const [createFormData, setCreateFormData] = useState({
    date_operation: new Date().toISOString().split('T')[0],
    gcr_prefix: 'GCR',
    gcr_code: '',
    operation: '',
    etat_final: getDefaultEtatForNewImputation(getTenantSubdomain()),
    commentaire: ''
  });

  // Form state for edit modal
  const [editFormData, setEditFormData] = useState({
    date_operation: '',
    gcr_prefix: 'GCR',
    gcr_code: '',
    operation: '',
    etat_final: '',
    commentaire: '',
    modification_reason: ''
  });

  // Project code: type-specific prefix and max digits (PROMO: T 5; GCR: INT-0000 7; GIR: TI000000 7; BYNOW: INC 10)
  const PROJECT_CODE_PREFIX_BY_TYPE = { GCR: 'INT-0000', GIR: 'TI000000', PROMO: 'T', BYNOW: 'INC' };
  const PROJECT_CODE_MAX_LENGTH_BY_TYPE = { GCR: 7, GIR: 7, PROMO: 5, BYNOW: 10 };
  const getProjectCodeMaxLength = (prefix) => PROJECT_CODE_MAX_LENGTH_BY_TYPE[prefix] ?? 7;

  const getEtatDisplay = (etat) => getEtatLabel(etat, getTenantSubdomain());

  // Filter operations based on search; MULTI-DOMAINE operations always appear (not filtered by search)
  const getFilteredOperations = (searchTerm) => {
    if (!searchTerm.trim()) return operations;
    const term = searchTerm.toLowerCase();
    const matching = operations.filter(op =>
      op.production_line_lp?.toLowerCase().includes(term) || op.activity?.toLowerCase().includes(term)
    );
    const multiDomaine = operations.filter(op =>
      op.domain && String(op.domain).toUpperCase().includes('MULTI-DOMAINE')
    );
    const seen = new Set();
    return [...matching, ...multiDomaine].filter(op => {
      if (seen.has(op.id)) return false;
      seen.add(op.id);
      return true;
    });
  };

  // Unique operation code prefixes (part before first "-", "_", or space) for chips
  const operationCodePrefixes = React.useMemo(() => {
    const prefixes = new Set();
    operations.forEach(op => {
      if (!op.production_line_lp) return;
      const prefix = String(op.production_line_lp).split(/[-_\s]/)[0]?.trim() || String(op.production_line_lp).trim();
      if (prefix) prefixes.add(prefix);
    });
    return Array.from(prefixes).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [operations]);

  // Apply filters to imputations list (including optional production line filter)
  const filteredImputations = imputations.filter((imp) => {
    if (filters.date_from && imp.date_operation < filters.date_from) return false;
    if (filters.date_to && imp.date_operation > filters.date_to) return false;
    if (filters.gcr && !imp.gcr.toLowerCase().includes(filters.gcr.toLowerCase())) return false;
    if (filters.operation && String(imp.operation.id) !== String(filters.operation)) return false;
    if (filters.production_line_lp) {
      const pl = imp.operation?.production_line_lp || '';
      if (!pl.toLowerCase().includes(filters.production_line_lp.toLowerCase())) return false;
    }
    return true;
  });

  // Lock workflow when selected operation has predefined workflow (create modal)
  const createSelectedOp = operations.find(o => o.id?.toString() === createFormData.operation);
  const isCreateProjectTypeLocked = createSelectedOp?.imputation_project_type_choice === 'PREDEFINED' && createSelectedOp?.default_project_type;

  // Lock workflow when selected operation has predefined workflow (edit modal)
  const editSelectedOp = operations.find(o => o.id?.toString() === editFormData.operation);
  const isEditProjectTypeLocked = editSelectedOp?.imputation_project_type_choice === 'PREDEFINED' && editSelectedOp?.default_project_type;

  // When reference is disabled for this operation, employees cannot set/edit project/workflow
  const isCreateReferenceDisabledForEmployee = currentUser?.role === 'EMPLOYE' && createSelectedOp?.is_reference_enabled === false;
  const isEditReferenceDisabledForEmployee = currentUser?.role === 'EMPLOYE' && editSelectedOp?.is_reference_enabled === false;
  // When no workflow is set for the operation, hide workflow and project code
  const hasCreateWorkflowsAssigned = createSelectedOp?.allowed_workflow_codes?.length > 0;
  const hasEditWorkflowsAssigned = editSelectedOp?.allowed_workflow_codes?.length > 0;
  const shouldHideCreateWorkflowAndProjectCode = isCreateReferenceDisabledForEmployee || (createSelectedOp && !hasCreateWorkflowsAssigned);
  const shouldHideEditWorkflowAndProjectCode = isEditReferenceDisabledForEmployee || (editSelectedOp && !hasEditWorkflowsAssigned);

  // Fetch current user for role check
  useEffect(() => {
    getCurrentUserProfile().then(setCurrentUser).catch(() => setCurrentUser(null));
  }, []);

  // Keep create form gcr_prefix in sync when operation is predefined
  useEffect(() => {
    if (isCreateProjectTypeLocked && createSelectedOp?.default_project_type && createFormData.gcr_prefix !== createSelectedOp.default_project_type) {
      setCreateFormData(prev => ({ ...prev, gcr_prefix: createSelectedOp.default_project_type }));
    }
  }, [isCreateProjectTypeLocked, createSelectedOp?.default_project_type]);

  // Keep edit form gcr_prefix in sync when operation is predefined
  useEffect(() => {
    if (isEditProjectTypeLocked && editSelectedOp?.default_project_type && editFormData.gcr_prefix !== editSelectedOp.default_project_type) {
      setEditFormData(prev => ({ ...prev, gcr_prefix: editSelectedOp.default_project_type }));
    }
  }, [isEditProjectTypeLocked, editSelectedOp?.default_project_type]);

  // Handle filter submit
  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  // Handle create imputation
  const handleCreateImputation = (e) => {
    e.preventDefault();
    // When reference is disabled or no workflows, use gcr "N/A" (backend accepts this)
    const payload = { ...createFormData };
    if (shouldHideCreateWorkflowAndProjectCode) {
      payload.gcr_for_api = 'N/A';
    }
    // Handle create logic here (when using API, send gcr: payload.gcr_for_api ?? builtGcr)
    console.log('Create imputation:', payload);
    setShowCreateModal(false);
    // Reset form
    setCreateFormData({
      date_operation: new Date().toISOString().split('T')[0],
      gcr_prefix: 'GCR',
      gcr_code: '',
      operation: '',
      etat_final: getDefaultEtatForNewImputation(getTenantSubdomain()),
      commentaire: ''
    });
  };

  // Handle edit imputation
  const handleEditImputation = (e) => {
    e.preventDefault();
    // When reference is disabled or no workflows, use N/A (backend accepts this)
    const payload = { ...editFormData };
    if (shouldHideEditWorkflowAndProjectCode) {
      payload.gcr_for_api = 'N/A';
    }
    // Handle edit logic here (when using API, send gcr: payload.gcr_for_api ?? builtGcr)
    console.log('Edit imputation:', payload);
    setShowEditModal(false);
  };

  // Open edit modal with imputation data
  const handleOpenEditModal = (imputation) => {
    // Parse GCR: type prefix + code prefix + type-specific digit suffix (e.g. GCR-INT-00001234567 → suffix 1234567)
    let gcrPrefix = 'GCR';
    let gcrCode = '';
    const validPrefixes = ['GCR', 'GIR', 'PROMO', 'BYNOW'];
    if (imputation.gcr && imputation.gcr.includes('-')) {
      const firstDash = imputation.gcr.indexOf('-');
      const parts = [imputation.gcr.slice(0, firstDash), imputation.gcr.slice(firstDash + 1)];
      if (validPrefixes.includes(parts[0])) {
        gcrPrefix = parts[0];
        const rest = parts[1];
        const codePrefix = PROJECT_CODE_PREFIX_BY_TYPE[gcrPrefix] || '';
        if (codePrefix && rest.startsWith(codePrefix)) {
          gcrCode = rest.slice(codePrefix.length);
        } else {
          gcrCode = rest;
        }
      } else {
        gcrCode = imputation.gcr;
      }
    } else if (imputation.gcr) {
      gcrCode = imputation.gcr;
    }

    const selectedOp = operations.find(op => op.id === imputation.operation.id);
    
    setEditFormData({
      date_operation: imputation.date_operation,
      gcr_prefix: gcrPrefix,
      gcr_code: gcrCode,
      operation: imputation.operation.id,
      etat_final: imputation.etat_final,
      commentaire: imputation.commentaire || '',
      modification_reason: ''
    });
    
    setOperationSearch(selectedOp ? `${selectedOp.production_line_lp} - ${selectedOp.activity}` : '');
    setSelectedImputation(imputation);
    setShowEditModal(true);
  };

  // Open operation details modal
  const handleOpenOperationDetails = (operation) => {
    setSelectedOperation(operation);
    setShowOperationDetailsModal(true);
  };

  // Render form container based on animation mode
  const FormContainer = useTransformersAnimation ? motion.div : 'div';
  const formContainerProps = useTransformersAnimation ? {
    layoutId: "form-container",
    layout: false,
    style: { willChange: 'height' }
  } : {};

  return (
    <div className="login-container">
      {/* Main content */}
      <main className="relative z-10 w-full px-0 py-0">
        <div 
          className="relative rounded-none transition-all duration-500 w-full transform group/card scale-100" 
          style={{ 
            transformOrigin: 'center center'
          }}
        >
          <FormContainer 
            className="glass-card rounded-none p-6 md:p-10 w-full relative z-10 flex flex-col" 
            {...formContainerProps}
          >
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight mt-8 md:mt-0">{t('imputations.title')}</h1>
                  <p className="text-gray-500 text-base">{t('imputations.subtitle')}</p>
                </div>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-6 py-3 bg-[#0A66C2] hover:bg-[#095195] text-white rounded-lg transition-colors duration-200 font-semibold shadow-lg hover:shadow-xl flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
                  </svg>
                  {t('imputations.newImputation')}
                </button>
              </div>
            </div>

            {/* Filters */}
            <div className="mb-6 bg-white shadow-lg rounded-xl p-6">
              <form onSubmit={handleFilterSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Date From */}
                  <div>
                    <label htmlFor="date_from" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('imputations.dateFrom')}
                    </label>
                    <input
                      type="date"
                      id="date_from"
                      name="date_from"
                      value={filters.date_from}
                      onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A66C2] focus:border-transparent transition-all duration-200 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100"
                    />
                  </div>

                  {/* Date To */}
                  <div>
                    <label htmlFor="date_to" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('imputations.dateTo')}
                    </label>
                    <input
                      type="date"
                      id="date_to"
                      name="date_to"
                      value={filters.date_to}
                      onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A66C2] focus:border-transparent transition-all duration-200 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100"
                    />
                  </div>

              {/* GCR / TT GIR Filter */}
                  <div>
                    <label htmlFor="gcr" className="block text-sm font-semibold text-gray-700 mb-2">
                      GCR / TT GIR
                    </label>
                    <input
                      type="text"
                      id="gcr"
                      name="gcr"
                      value={filters.gcr}
                      onChange={(e) => setFilters({ ...filters, gcr: e.target.value })}
                      placeholder={t('imputations.enterGcr')}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A66C2] focus:border-transparent transition-all duration-200 placeholder-gray-400 dark:placeholder-slate-400 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100"
                    />
                  </div>
              {/* Production Line (PL) Filter */}
              <div>
                <label htmlFor="production_line_lp" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('imputations.productionLinePL')}
                </label>
                <input
                  type="text"
                  id="production_line_lp"
                  name="production_line_lp"
                  value={filters.production_line_lp}
                  onChange={(e) => setFilters({ ...filters, production_line_lp: e.target.value })}
                  placeholder={t('imputations.enterProductionLine')}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A66C2] focus:border-transparent transition-all duration-200 placeholder-gray-400 dark:placeholder-slate-400 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100"
                />
              </div>

              {/* Operation Type Filter - Searchable Dropdown */}
              <div className="relative">
                    <label htmlFor="filter_operation_search" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('imputations.operation')}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="filter_operation_search"
                        value={filterOperationSearch}
                        onChange={(e) => {
                          setFilterOperationSearch(e.target.value);
                          setShowFilterOperationDropdown(true);
                        }}
                        onFocus={() => setShowFilterOperationDropdown(true)}
                        placeholder={t('imputations.searchOrSelectOperation')}
                        className="w-full px-4 py-2 pr-10 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A66C2] focus:border-transparent transition-all duration-200 placeholder-gray-400 dark:placeholder-slate-400 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                        </svg>
                      </div>
                      
                      {showFilterOperationDropdown && (
                        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg shadow-lg max-h-60 overflow-auto">
                          <div
                            onClick={() => {
                              setFilters({ ...filters, operation: '' });
                              setFilterOperationSearch('');
                              setShowFilterOperationDropdown(false);
                            }}
                            className="px-4 py-3 hover:bg-blue-50 cursor-pointer transition-colors duration-150 border-b border-gray-100 font-medium text-gray-700"
                          >
                            {t('imputations.allOperations')}
                          </div>
                          {getFilteredOperations(filterOperationSearch).map(op => (
                            <div
                              key={op.id}
                              onClick={() => {
                                setFilters({ ...filters, operation: op.id.toString() });
                                setFilterOperationSearch(`${op.production_line_lp} - ${op.activity}`);
                                setShowFilterOperationDropdown(false);
                              }}
                              className="px-4 py-3 hover:bg-blue-50 cursor-pointer transition-colors duration-150 border-b border-gray-100 last:border-b-0"
                            >
                              <div className="font-medium text-gray-900">{op.production_line_lp} - {op.activity}</div>
                            </div>
                          ))}
                          {getFilteredOperations(filterOperationSearch).length === 0 && (
                            <div className="px-4 py-3 text-sm text-gray-500">{t('imputations.noOperationsFound')}</div>
                          )}
                        </div>
                      )}
                    </div>
                    <select
                      id="filter_operation"
                      name="operation"
                      value={filters.operation}
                      onChange={(e) => setFilters({ ...filters, operation: e.target.value })}
                      className="hidden"
                    >
                      <option value="">{t('imputations.allOperations')}</option>
                      {operations.map(op => (
                        <option key={op.id} value={op.id}>{op.production_line_lp} - {op.activity}</option>
                      ))}
                    </select>
              </div>
                </div>

                {/* Filter Buttons */}
                <div className="flex items-center justify-end gap-3">
                  <button
                    type="submit"
                    className="px-6 py-2 bg-[#0A66C2] hover:bg-[#095195] text-white rounded-lg transition-colors duration-200 font-semibold shadow-md hover:shadow-lg flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/>
                    </svg>
                    {t('imputations.applyFilters')}
                  </button>
                  {(filters.date_from || filters.date_to || filters.gcr || filters.operation) && (
                    <button
                      type="button"
                      onClick={() => {
                        setFilters({ date_from: '', date_to: '', gcr: '', operation: '' });
                        setFilterOperationSearch('');
                        setCurrentPage(1);
                      }}
                      className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200 font-semibold flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                      {t('imputations.clearFilters')}
                    </button>
                  )}
                </div>
              </form>
            </div>

            {/* Imputations Table */}
            {filteredImputations.length > 0 ? (
              <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{t('imputations.id')}</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{t('imputations.dateOperation')}</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">GCR / TT GIR</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{t('imputations.operation')}</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{t('imputations.etat')}</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{t('imputations.tempsStandard')}</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{t('imputations.dateSaisie')}</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{t('imputations.actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredImputations.map((imputation) => (
                        <motion.tr
                          key={imputation.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                          className="hover:bg-gray-50 transition-colors duration-150"
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            #{imputation.id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {new Date(imputation.date_operation).toLocaleDateString(getDateLocale(i18n.language))}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {imputation.gcr}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            <button
                              type="button"
                              onClick={() => handleOpenOperationDetails(imputation.operation)}
                              className="text-left hover:text-[#0A66C2] transition-colors duration-200 cursor-pointer group"
                            >
                              <div className="font-medium group-hover:underline">{imputation.operation.production_line_lp}</div>
                              <div className="text-xs text-gray-500 group-hover:text-[#0A66C2]">{imputation.operation.activity}</div>
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getEtatBadgeClasses(imputation.etat_final, getTenantSubdomain())}`}>
                              {getEtatDisplay(imputation.etat_final)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {imputation.temps_standard}h
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(imputation.date_saisie).toLocaleString(getDateLocale(i18n.language))}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {imputation.can_be_modified ? (
                              <button
                                type="button"
                                onClick={() => handleOpenEditModal(imputation)}
                                className="inline-flex items-center px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-md transition-colors duration-200 text-xs font-medium"
                              >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                                </svg>
                                {tCommon('edit')}
                              </button>
                            ) : (
                              <span className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-500 rounded-md text-xs font-medium cursor-not-allowed" title={t('imputations.lockedTitle')}>
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                                </svg>
                                {t('imputations.locked')}
                              </span>
                            )}
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="bg-white shadow-xl rounded-2xl p-12 text-center">
                <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('imputations.noImputations')}</h3>
                <p className="text-sm text-gray-600 mb-6">{t('imputations.emptyHint')}</p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center px-6 py-3 bg-[#0A66C2] hover:bg-[#095195] text-white rounded-lg transition-colors duration-200 font-semibold shadow-lg hover:shadow-xl"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
                  </svg>
                  {t('imputations.createFirst')}
                </button>
              </div>
            )}

          </FormContainer>
        </div>
      </main>

      {/* Create Imputation Modal - Will be added in next part */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">{t('imputations.createTitle')}</h2>
                <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6">
              <form onSubmit={handleCreateImputation} className="space-y-6">
                {/* Operation Date */}
                <div>
                  <label htmlFor="modal_date_operation" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('imputations.date')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    id="modal_date_operation"
                    name="date_operation"
                    value={createFormData.date_operation}
                    onChange={(e) => setCreateFormData({ ...createFormData, date_operation: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A66C2] focus:border-transparent transition-all duration-200 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100"
                  />
                </div>

                {/* Workflow & Project Code — hidden when reference disabled or no workflows */}
                {shouldHideCreateWorkflowAndProjectCode ? (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <p className="text-sm text-gray-600">
                      {t('imputations.referenceNotUsed')}
                    </p>
                  </div>
                ) : (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('imputations.workflow')} <span className="text-red-500">*</span>
                    {isCreateProjectTypeLocked && (
                      <span className="ml-2 text-xs font-normal text-gray-500">{t('imputations.fixedForOperation')}</span>
                    )}
                  </label>
                  <select
                    id="modal_gcr_prefix"
                    name="gcr_prefix"
                    value={createFormData.gcr_prefix}
                    onChange={(e) => !isCreateProjectTypeLocked && setCreateFormData({ ...createFormData, gcr_prefix: e.target.value })}
                    required
                    disabled={isCreateProjectTypeLocked}
                    className={`w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A66C2] focus:border-transparent transition-all duration-200 mb-4 ${isCreateProjectTypeLocked ? 'bg-gray-100 dark:bg-slate-700 cursor-not-allowed text-gray-700 dark:text-slate-300' : 'bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100'}`}
                  >
                    <option value="GCR">GCR (INT-0000)</option>
                    <option value="GIR">TT GIR (TI000000)</option>
                    <option value="PROMO">PROMO (T)</option>
                    <option value="BYNOW">BYNOW (INC)</option>
                  </select>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('imputations.projectCode')} <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center rounded-lg border border-gray-300 overflow-hidden focus-within:ring-2 focus-within:ring-[#0A66C2] focus-within:border-transparent bg-white">
                    <span className="inline-flex items-center h-12 px-4 bg-gray-100 text-gray-700 font-mono text-sm border-r border-gray-300 shrink-0">
                      {PROJECT_CODE_PREFIX_BY_TYPE[createFormData.gcr_prefix] || ''}
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      id="modal_gcr_code"
                      name="gcr_code"
                      value={createFormData.gcr_code}
                      onChange={(e) => {
                        const maxLen = getProjectCodeMaxLength(createFormData.gcr_prefix);
                        const digits = e.target.value.replace(/\D/g, '').slice(0, maxLen);
                        setCreateFormData({ ...createFormData, gcr_code: digits });
                      }}
                      required
                      maxLength={getProjectCodeMaxLength(createFormData.gcr_prefix)}
                      placeholder={t('imputations.projectCodePlaceholder', { count: getProjectCodeMaxLength(createFormData.gcr_prefix) })}
                      className="flex-1 h-12 px-4 font-mono placeholder-gray-400 outline-none text-gray-900"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{t('imputations.projectCodeHint', { count: getProjectCodeMaxLength(createFormData.gcr_prefix) })}</p>
                </div>
                )}

                {/* Operation - Searchable Dropdown */}
                <div className="relative">
                  <label htmlFor="modal_operation_search" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('imputations.operation')} <span className="text-red-500">*</span>
                  </label>
                  {operationCodePrefixes.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {operationCodePrefixes.map((prefix) => (
                        <button
                          key={prefix}
                          type="button"
                          onClick={() => {
                            setOperationSearch(prefix + '-');
                            setShowOperationDropdown(true);
                          }}
                          className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-blue-50 hover:text-[#0A66C2] border border-gray-200 transition-colors"
                        >
                          {prefix}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="relative">
                    <input
                      type="text"
                      id="modal_operation_search"
                      value={operationSearch}
                      onChange={(e) => {
                        setOperationSearch(e.target.value);
                        setShowOperationDropdown(true);
                      }}
                      onFocus={() => setShowOperationDropdown(true)}
                      placeholder={t('imputations.searchOperations')}
                      className="w-full px-4 py-3 pr-10 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A66C2] focus:border-transparent transition-all duration-200 placeholder-gray-400 dark:placeholder-slate-400 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                      </svg>
                    </div>
                    
                    {showOperationDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg shadow-lg max-h-60 overflow-auto">
                        {getFilteredOperations(operationSearch).map(op => (
                          <div
                            key={op.id}
                            onClick={() => {
                              setCreateFormData({
                                ...createFormData,
                                operation: op.id.toString(),
                                ...(op.imputation_project_type_choice === 'PREDEFINED' && op.default_project_type ? { gcr_prefix: op.default_project_type } : {})
                              });
                              setOperationSearch(`${op.production_line_lp} - ${op.activity}`);
                              setShowOperationDropdown(false);
                            }}
                            className="px-4 py-3 hover:bg-blue-50 cursor-pointer transition-colors duration-150 border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900">{op.production_line_lp} - {op.activity}</div>
                            <div className="text-xs text-gray-500 mt-1">{t('imputations.standardTimeLabel', { time: op.temps_standard })}</div>
                          </div>
                        ))}
                        {getFilteredOperations(operationSearch).length === 0 && (
                          <div className="px-4 py-3 text-sm text-gray-500">{t('imputations.noOperationsFound')}</div>
                        )}
                      </div>
                    )}
                  </div>
                  <select
                    id="modal_operation"
                    name="operation"
                    value={createFormData.operation}
                    onChange={(e) => setCreateFormData({ ...createFormData, operation: e.target.value })}
                    required
                    className="hidden"
                  >
                    <option value="">{t('imputations.selectOperation')}</option>
                    {operations.map(op => (
                      <option key={op.id} value={op.id}>{op.production_line_lp} - {op.activity}</option>
                    ))}
                  </select>
                </div>

                {/* Final Operation State */}
                <div>
                  <label htmlFor="modal_etat_final" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('imputations.finalOperationState')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="modal_etat_final"
                    name="etat_final"
                    value={createFormData.etat_final}
                    onChange={(e) => setCreateFormData({ ...createFormData, etat_final: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A66C2] focus:border-transparent transition-all duration-200 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100"
                  >
                    {etatChoices.map(choice => (
                      <option key={choice.value} value={choice.value}>{choice.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('imputations.echecNote')}
                  </p>
                </div>

                {/* Comment */}
                <div>
                  <label htmlFor="modal_commentaire" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('imputations.comment')} <span className="text-gray-400 text-xs">{t('imputations.optional')}</span>
                  </label>
                  <textarea
                    id="modal_commentaire"
                    name="commentaire"
                    rows="4"
                    value={createFormData.commentaire}
                    onChange={(e) => setCreateFormData({ ...createFormData, commentaire: e.target.value })}
                    placeholder={t('imputations.commentPlaceholder')}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A66C2] focus:border-transparent transition-all duration-200 placeholder-gray-400 dark:placeholder-slate-400 resize-none bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100"
                  />
                </div>

                {/* Submit Button */}
                <div className="flex items-center justify-end gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200 font-semibold"
                  >
                    {tCommon('cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 bg-[#0A66C2] hover:bg-[#095195] text-white rounded-lg transition-colors duration-200 font-semibold shadow-lg hover:shadow-xl"
                  >
                    {t('imputations.create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Imputation Modal - Will be added */}
      {showEditModal && selectedImputation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">{t('imputations.edit')}</h2>
                <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Info Message */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 dark:bg-blue-900/30 dark:border-blue-700/50 dark:text-blue-200 rounded-lg">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-blue-800">{t('imputations.modificationAuthorized')}</p>
                    <p className="text-xs text-blue-700 mt-1">{t('imputations.modificationAuthorizedBody')}</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleEditImputation} className="space-y-6">
                {/* Similar fields as create modal */}
                {/* Operation Date */}
                <div>
                  <label htmlFor="edit_modal_date_operation" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('imputations.date')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    id="edit_modal_date_operation"
                    name="date_operation"
                    value={editFormData.date_operation}
                    onChange={(e) => setEditFormData({ ...editFormData, date_operation: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A66C2] focus:border-transparent transition-all duration-200 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100"
                  />
                </div>

                {/* Workflow & Project Code — hidden when reference disabled or no workflows */}
                {shouldHideEditWorkflowAndProjectCode ? (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <p className="text-sm text-gray-600">
                      {t('imputations.referenceNotEditable')}
                    </p>
                  </div>
                ) : (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('imputations.workflow')} <span className="text-red-500">*</span>
                    {isEditProjectTypeLocked && (
                      <span className="ml-2 text-xs font-normal text-gray-500">{t('imputations.fixedForOperation')}</span>
                    )}
                  </label>
                  <select
                    id="edit_modal_gcr_prefix"
                    name="gcr_prefix"
                    value={editFormData.gcr_prefix}
                    onChange={(e) => !isEditProjectTypeLocked && setEditFormData({ ...editFormData, gcr_prefix: e.target.value })}
                    required
                    disabled={isEditProjectTypeLocked}
                    className={`w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A66C2] focus:border-transparent transition-all duration-200 mb-4 ${isEditProjectTypeLocked ? 'bg-gray-100 dark:bg-slate-700 cursor-not-allowed text-gray-700 dark:text-slate-300' : 'bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100'}`}
                  >
                    <option value="GCR">GCR (INT-0000)</option>
                    <option value="GIR">TT GIR (TI000000)</option>
                    <option value="PROMO">PROMO (T)</option>
                    <option value="BYNOW">BYNOW (INC)</option>
                  </select>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('imputations.projectCode')} <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center rounded-lg border border-gray-300 overflow-hidden focus-within:ring-2 focus-within:ring-[#0A66C2] focus-within:border-transparent bg-white">
                    <span className="inline-flex items-center h-12 px-4 bg-gray-100 text-gray-700 font-mono text-sm border-r border-gray-300 shrink-0">
                      {PROJECT_CODE_PREFIX_BY_TYPE[editFormData.gcr_prefix] || ''}
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      id="edit_modal_gcr_code"
                      name="gcr_code"
                      value={editFormData.gcr_code}
                      onChange={(e) => {
                        const maxLen = getProjectCodeMaxLength(editFormData.gcr_prefix);
                        const digits = e.target.value.replace(/\D/g, '').slice(0, maxLen);
                        setEditFormData({ ...editFormData, gcr_code: digits });
                      }}
                      required
                      maxLength={getProjectCodeMaxLength(editFormData.gcr_prefix)}
                      placeholder={t('imputations.projectCodePlaceholder', { count: getProjectCodeMaxLength(editFormData.gcr_prefix) })}
                      className="flex-1 h-12 px-4 font-mono placeholder-gray-400 outline-none text-gray-900"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{t('imputations.projectCodeHint', { count: getProjectCodeMaxLength(editFormData.gcr_prefix) })}</p>
                </div>
                )}

                {/* Operation - Searchable Dropdown */}
                <div className="relative">
                  <label htmlFor="edit_modal_operation_search" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('imputations.operation')} <span className="text-red-500">*</span>
                  </label>
                  {operationCodePrefixes.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {operationCodePrefixes.map((prefix) => (
                        <button
                          key={prefix}
                          type="button"
                          onClick={() => {
                            setOperationSearch(prefix + '-');
                            setShowOperationDropdown(true);
                          }}
                          className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-blue-50 hover:text-[#0A66C2] border border-gray-200 transition-colors"
                        >
                          {prefix}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="relative">
                    <input
                      type="text"
                      id="edit_modal_operation_search"
                      value={operationSearch}
                      onChange={(e) => {
                        setOperationSearch(e.target.value);
                        setShowOperationDropdown(true);
                      }}
                      onFocus={() => setShowOperationDropdown(true)}
                      placeholder={t('imputations.searchOperations')}
                      className="w-full px-4 py-3 pr-10 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A66C2] focus:border-transparent transition-all duration-200 placeholder-gray-400 dark:placeholder-slate-400 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                      </svg>
                    </div>
                    
                    {showOperationDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg shadow-lg max-h-60 overflow-auto">
                        {getFilteredOperations(operationSearch).map(op => (
                          <div
                            key={op.id}
                            onClick={() => {
                              setEditFormData({
                                ...editFormData,
                                operation: op.id.toString(),
                                ...(op.imputation_project_type_choice === 'PREDEFINED' && op.default_project_type ? { gcr_prefix: op.default_project_type } : {})
                              });
                              setOperationSearch(`${op.production_line_lp} - ${op.activity}`);
                              setShowOperationDropdown(false);
                            }}
                            className="px-4 py-3 hover:bg-blue-50 cursor-pointer transition-colors duration-150 border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900">{op.production_line_lp} - {op.activity}</div>
                            <div className="text-xs text-gray-500 mt-1">{t('imputations.standardTimeLabel', { time: op.temps_standard })}</div>
                          </div>
                        ))}
                        {getFilteredOperations(operationSearch).length === 0 && (
                          <div className="px-4 py-3 text-sm text-gray-500">{t('imputations.noOperationsFound')}</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Final Operation State */}
                <div>
                  <label htmlFor="edit_modal_etat_final" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('imputations.finalOperationState')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="edit_modal_etat_final"
                    name="etat_final"
                    value={editFormData.etat_final}
                    onChange={(e) => setEditFormData({ ...editFormData, etat_final: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A66C2] focus:border-transparent transition-all duration-200 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100"
                  >
                    {etatChoices.map(choice => (
                      <option key={choice.value} value={choice.value}>{choice.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('imputations.echecNote')}
                  </p>
                </div>

                {/* Comment */}
                <div>
                  <label htmlFor="edit_modal_commentaire" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('imputations.comment')} <span className="text-gray-400 text-xs">{t('imputations.optional')}</span>
                  </label>
                  <textarea
                    id="edit_modal_commentaire"
                    name="commentaire"
                    rows="4"
                    value={editFormData.commentaire}
                    onChange={(e) => setEditFormData({ ...editFormData, commentaire: e.target.value })}
                    placeholder={t('imputations.commentPlaceholder')}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A66C2] focus:border-transparent transition-all duration-200 placeholder-gray-400 dark:placeholder-slate-400 resize-none bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100"
                  />
                </div>

                {/* Modification Reason */}
                <div>
                  <label htmlFor="edit_modal_modification_reason" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('imputations.modificationReason')} <span className="text-gray-400 text-xs">{t('imputations.optionalRecommended')}</span>
                  </label>
                  <textarea
                    id="edit_modal_modification_reason"
                    name="modification_reason"
                    rows="3"
                    value={editFormData.modification_reason}
                    onChange={(e) => setEditFormData({ ...editFormData, modification_reason: e.target.value })}
                    placeholder={t('imputations.modificationReasonPlaceholder')}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A66C2] focus:border-transparent transition-all duration-200 placeholder-gray-400 dark:placeholder-slate-400 resize-none bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('imputations.modificationReasonAudit')}
                  </p>
                </div>

                {/* Submit Button */}
                <div className="flex items-center justify-end gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200 font-semibold"
                  >
                    {tCommon('cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 bg-[#0A66C2] hover:bg-[#095195] text-white rounded-lg transition-colors duration-200 font-semibold shadow-lg hover:shadow-xl"
                  >
                    {t('imputations.save')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Operation Details Modal */}
      {showOperationDetailsModal && selectedOperation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">{t('imputations.operationDetails')}</h2>
                <button onClick={() => setShowOperationDetailsModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Operation Code */}
              <div>
                <label className="block text-sm font-semibold text-gray-500 mb-1">{t('imputations.operationCodeLabel')}</label>
                <p className="text-lg font-bold text-gray-900">{selectedOperation.production_line_lp || tCommon('na')}</p>
              </div>

              {/* Operation Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-500 mb-1">{t('imputations.operationName')}</label>
                <p className="text-base text-gray-700">{selectedOperation.activity || tCommon('na')}</p>
              </div>

              {/* Standard Time */}
              <div>
                <label className="block text-sm font-semibold text-gray-500 mb-1">{t('imputations.standardTime')}</label>
                <p className="text-base text-gray-700">{t('imputations.hoursUnit', { count: selectedOperation.temps_standard || '0' })}</p>
              </div>

              {/* Description */}
              {selectedOperation.description && (
                <div>
                  <label className="block text-sm font-semibold text-gray-500 mb-1">{tCommon('description')}</label>
                  <p className="text-base text-gray-700 whitespace-pre-wrap">{selectedOperation.description}</p>
                </div>
              )}

              {/* Status */}
              <div>
                <label className="block text-sm font-semibold text-gray-500 mb-1">{tCommon('status')}</label>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                  selectedOperation.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {selectedOperation.is_active ? tCommon('active') : tCommon('inactive')}
                </span>
              </div>

              {/* Close Button */}
              <div className="flex items-center justify-end pt-4">
                <button
                  type="button"
                  onClick={() => setShowOperationDetailsModal(false)}
                  className="px-6 py-3 bg-[#0A66C2] hover:bg-[#095195] text-white rounded-lg transition-colors duration-200 font-semibold shadow-lg hover:shadow-xl"
                >
                  {tCommon('close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyImputations;
